import { useState, useEffect, useCallback } from "react";
import api from "../../api/api";
import { formatRp } from "../../utils/format";
import { formatDateTime } from "../../utils/datetime";
import { useDialog } from "../../components/DialogProvider";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

export default function AccountantReconciliations() {
  const { toast } = useDialog();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [selectedSession, setSelectedSession] = useState(null);
  const [actualAmount, setActualAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchReconciliations = useCallback(async () => {
    try {
      const res = await api.get("/accountant/reconciliations");
      setData(res.data.data || []);
    } catch (err) {
      toast({ type: "error", title: "Error", message: "Gagal memuat data rekonsiliasi." });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchReconciliations();
  }, [fetchReconciliations]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSession) return;
    if (actualAmount === "") {
      toast({ type: "error", title: "Error", message: "Nominal uang fisik wajib diisi." });
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/accountant/reconciliations", {
        session_id: selectedSession.session_id,
        system_amount: selectedSession.session_total_sales,
        actual_amount: actualAmount,
        note: note,
      });
      
      toast({ type: "success", title: "Sukses", message: "Rekonsiliasi berhasil disimpan." });
      setSelectedSession(null);
      setActualAmount("");
      setNote("");
      fetchReconciliations();
    } catch (err) {
      toast({ type: "error", title: "Gagal", message: err.response?.data?.message || "Gagal menyimpan rekonsiliasi." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10 max-w-[1400px]">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Cek Setoran Kasir</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Bandingkan uang hasil penjualan di sistem dengan uang laci (fisik) yang disetorkan kasir.
        </p>
      </header>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-[13px]">
            <thead className="bg-zinc-50 border-b border-zinc-200 text-zinc-600">
              <tr>
                <th className="px-4 py-3 font-medium">Kasir & Event</th>
                <th className="px-4 py-3 font-medium text-right">Harusnya (Sistem)</th>
                <th className="px-4 py-3 font-medium text-right">Setoran (Fisik)</th>
                <th className="px-4 py-3 font-medium text-right">Selisih</th>
                <th className="px-4 py-3 font-medium text-center">Status</th>
                <th className="px-4 py-3 font-medium text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-zinc-500">Memuat data...</td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-zinc-500">Belum ada data rekonsiliasi.</td>
                </tr>
              ) : (
                data.map((row) => (
                  <tr key={row.id} className="hover:bg-zinc-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-zinc-900">{row.event_name}</div>
                      <div className="text-[11px] text-zinc-500 font-mono mt-0.5">Sesi #{row.session_id} • Tutup: {formatDateTime(row.closed_at)}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-zinc-900 font-medium">
                      {formatRp(row.session_total_sales)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-zinc-900 font-medium">
                      {row.status === 'pending' ? '-' : formatRp(row.actual_amount)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-medium">
                      {row.status === 'pending' ? '-' : (
                        <span className={Number(row.discrepancy) < 0 ? "text-rose-600" : Number(row.discrepancy) > 0 ? "text-emerald-600" : "text-zinc-400"}>
                          {Number(row.discrepancy) > 0 ? "+" : ""}{formatRp(row.discrepancy)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row.status === 'verified' ? (
                        <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                          <CheckCircle2 className="h-3 w-3" /> Cocok
                        </span>
                      ) : row.status === 'discrepancy_noted' ? (
                        <span className="inline-flex items-center gap-1 rounded-md bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-600/20">
                          <AlertCircle className="h-3 w-3" /> Selisih
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-600/20">
                          Belum Cek
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => {
                          setSelectedSession(row);
                          setActualAmount(row.status === 'pending' ? '' : row.actual_amount);
                          setNote(row.note || '');
                        }}
                        className="inline-flex items-center justify-center rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-[11px] font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors"
                      >
                        {row.status === 'pending' ? 'Verifikasi' : 'Edit Hasil'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Verifikasi Modal */}
      {selectedSession && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-zinc-950/50 backdrop-blur-sm animate-fade-in">
          <div className="relative w-full max-w-md rounded-lg border border-zinc-200 bg-white shadow-xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-zinc-100 px-5 py-4 bg-white">
              <h3 className="text-sm font-semibold text-zinc-900">Validasi Setoran (Sesi #{selectedSession.session_id})</h3>
              <p className="text-[12px] text-zinc-500 mt-1">Sistem mencatat: <strong className="text-zinc-900 tabular-nums">{formatRp(selectedSession.session_total_sales)}</strong></p>
            </div>
            
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Uang Fisik Diterima</label>
                <input
                  type="number"
                  required
                  value={actualAmount}
                  onChange={(e) => setActualAmount(e.target.value)}
                  className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm"
                  placeholder="Masukkan jumlah fisik..."
                />
                {actualAmount !== "" && (
                  <div className={`mt-2 rounded-md p-2 border text-[11px] ${Number(actualAmount) - Number(selectedSession.session_total_sales) < 0 ? 'border-rose-200 bg-rose-50 text-rose-700' : Number(actualAmount) - Number(selectedSession.session_total_sales) > 0 ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-zinc-200 bg-zinc-50 text-zinc-600'}`}>
                    Selisih dihitung: <span className="font-bold font-mono">{formatRp(Number(actualAmount) - Number(selectedSession.session_total_sales))}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Catatan / Alasan</label>
                <textarea
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm min-h-[80px]"
                  placeholder="Tulis alasan khusus jika ada perbedaan jumlah..."
                />
              </div>
            </form>
            
            <div className="border-t border-zinc-100 bg-zinc-50/50 px-5 py-3 flex justify-end gap-2">
              <button type="button" onClick={() => setSelectedSession(null)} className="rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-[13px] font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors" disabled={submitting}>Batal</button>
              <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-md bg-zinc-900 px-3 py-1.5 text-[13px] font-medium text-white shadow-sm hover:bg-zinc-800 transition-colors disabled:opacity-50" disabled={submitting}>
                {submitting && <Loader2 className="h-3 w-3 animate-spin" />}
                Simpan Validasi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
